import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import { imagePropTypes } from '../../../propTypes';
import { getErrorMessageKey, getVynePaymentErrorMessageKey } from '../../../util/errorCodes';
import jsonPost from '../../../util/jsonPost';
import {
  findCurrencyItemFromListWithFallback,
  getInitialCurrencyState,
} from '../../../util/currency';

import Button from '../Atoms/Button';
import MemberAddress from '../BookingFieldset/MemberAddress';
import MemberDetails from '../BookingFieldset/MemberDetails';
import { leadBookerPropTypes } from '../BookingFieldset/PropTypes';
import PaymentOptions from '../BookingSummary/PaymentOptions';
import TermsConditions from '../BookingSummary/TermsConditions';
import { withErrorBoundary } from '../Core/ErrorBoundary';
import ErrorMessage from '../Core/ErrorMessage';
import PackageSummary from '../PackageSummary';
import StickyComponent from '../StickyComponent';
import CUSTOM_EVENT from '../../../util/customEvents';

const propTypes = exact({
  apiURL: PropTypes.string.isRequired,
  data: PropTypes.shape({
    bankTransferPaymentsEnabled: PropTypes.bool,
    errorCode: PropTypes.number,
    errorMessage: PropTypes.string,
    marketingSignUp1: PropTypes.bool,
    marketingSignUp2: PropTypes.bool,
    marketingSignUp3: PropTypes.bool,
    marketingSignUp4: PropTypes.bool,
    papOption: PropTypes.shape({
      costPerPerson: PropTypes.number,
      description: PropTypes.string,
      isCallToBook: PropTypes.bool,
      isSoldOut: PropTypes.bool,
      key: PropTypes.string,
      maxPartySize: PropTypes.number,
      title: PropTypes.string,
    }),
    papQuantity: PropTypes.number,
    paymentUser: leadBookerPropTypes,
    totalAmount: PropTypes.number,
    pricePerCurrency: PropTypes.arrayOf(
      PropTypes.shape({
        current: PropTypes.string,
        totalPrice: PropTypes.number,
      })
    ),
  }),
  labels: PropTypes.shape({
    addressFieldsetTitle: PropTypes.string,
    bankTransferNoConsentMessage: PropTypes.string,
    bankTransferNoConfirmMessage: PropTypes.string,
    bankTransferExpiredMessage: PropTypes.string,
    bankTransferFailedMessage: PropTypes.string,
    callToBookText: PropTypes.string,
    failedToRetrieveCheckoutProcessSettingsMessage: PropTypes.string,
    invalidPaymentOptionMessage: PropTypes.string,
    invalidPriorityAccessPassIdErrorMessage: PropTypes.string,
    leadPartyMemberFieldsetSubTitle: PropTypes.string,
    leadPartyMemberFieldsetTitle: PropTypes.string,
    marketingSignUp1LabelText: PropTypes.string,
    marketingSignUp2LabelText: PropTypes.string,
    marketingSignUp3LabelText: PropTypes.string,
    marketingSignUp4LabelText: PropTypes.string,
    papSelectionLabel: PropTypes.string,
    partyDetailsFieldsetHeaderText: PropTypes.string,
    paymentFieldsetTitle: PropTypes.string,
    paymentOptionBankTransferPayInFullText: PropTypes.string,
    paymentOptionCardPayInFullText: PropTypes.string,
    preferredCardImage: imagePropTypes,
    submitButtonText: PropTypes.string,
    termsConditionsHeaderText: PropTypes.string,
    termsConditionsLabelText: PropTypes.string,
    termsConditionsText: PropTypes.string,
    titleOptions: PropTypes.arrayOf(PropTypes.string),
    totalPriceText: PropTypes.string,
    totalToPayText: PropTypes.string,
    validationAcceptTermsAndConditions: PropTypes.string,
    placeholderTexts: {
      addressLine2PlaceholderText: PropTypes.string,
      telephonePlaceholderText: PropTypes.string,
    },
  }),
});

const defaultProps = {
  data: undefined,
  labels: undefined,
};

class PapBookingSummary extends Component {
  constructor(props) {
    super(props);

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleTermsConditionsChange = this.handleTermsConditionsChange.bind(this);
    this.handlePaymentOptionsChange = this.handlePaymentOptionsChange.bind(this);

    this.submitContainerRef = React.createRef();

    const { data } = props;

    const currentCurrency = getInitialCurrencyState(data.pricePerCurrency);

    const currentPrice = findCurrencyItemFromListWithFallback(
      data.pricePerCurrency,
      currentCurrency.code
    );

    this.state = {
      errorMessage: null,
      isLoading: false,
      marketingSignUp1: data.marketingSignUp1,
      marketingSignUp2: data.marketingSignUp2,
      marketingSignUp3: data.marketingSignUp3,
      marketingSignUp4: data.marketingSignUp4,
      termsConditionsAccepted: false,
      paymentOption: '0',
      price: currentPrice.totalPrice,
      currentCurrency,
    };
  }

  componentDidMount() {
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
    const urlSearchParams = new URLSearchParams(window.location.search);
    const codeQueryValue = urlSearchParams.get('Code') || urlSearchParams.get('code');

    if (codeQueryValue) {
      const errorMessageKey = getVynePaymentErrorMessageKey(Number(codeQueryValue));

      this.setState(
        {
          errorMessage: this.props.labels[errorMessageKey],
        },
        () => {
          if (this.submitContainerRef.current && this.state.errorMessage) {
            window.scrollTo({ top: this.submitContainerRef.current.getBoundingClientRect().top });
          }
        }
      );
    }
  }

  componentWillUnmount() {
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  handleCurrencyChange = (event) => {
    const userSelectedCurrency = event?.detail;
    const { data } = this.props;

    // Find the now selected currency in our currency list from the BE
    const newCurrency = findCurrencyItemFromListWithFallback(
      data.pricePerCurrency,
      userSelectedCurrency.code
    );

    this.setState({
      price: newCurrency.totalPrice,
      currentCurrency: userSelectedCurrency,
    });
  };

  handleTermsConditionsChange(event) {
    const { checked, name } = event.target;
    this.setState({ [name]: checked });
  }

  handlePaymentOptionsChange(paymentOption) {
    this.setState({ paymentOption });
  }

  handleSubmit(event) {
    event.preventDefault();

    if (!this.state.termsConditionsAccepted) {
      return this.setState({ errorMessage: this.props.labels.validationAcceptTermsAndConditions });
    }

    this.setState({
      errorMessage: null,
      isLoading: true,
    });

    const options = {
      marketingSignUp1: this.state.marketingSignUp1,
      marketingSignUp2: this.state.marketingSignUp2,
      marketingSignUp3: this.state.marketingSignUp3,
      marketingSignUp4: this.state.marketingSignUp4,
      termsAndConditions: this.state.termsConditionsAccepted,
      paymentOption: Number(this.state.paymentOption),
    };

    return jsonPost(this.props.apiURL, options, (response) => {
      if (response.errorCode || response.errors) {
        const errorMessageKey = getErrorMessageKey(response.errorCode);
        return this.setState({
          errorMessage:
            this.props.labels[errorMessageKey] || response.message || 'An error occurred',
          isLoading: false,
        });
      }

      window.location.href = response.redirectUri;
      return this.setState({ errorMessage: null });
    });
  }

  render() {
    const { data, labels } = this.props;
    const {
      errorMessage,
      isLoading,
      marketingSignUp1,
      marketingSignUp2,
      marketingSignUp3,
      marketingSignUp4,
      termsConditionsAccepted,
      price,
      currentCurrency,
    } = this.state;

    return (
      <section className="Booking">
        <form noValidate autoComplete="false" onSubmit={this.handleSubmit}>
          {/* Package summary */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <StickyComponent contained>
                <PackageSummary
                  currencySymbol={currentCurrency.symbol}
                  description={data.papOption.description}
                  preferredCardImage={labels.preferredCardImage}
                  price={price}
                  title={data.papOption.title}
                  secondDescription={`${labels.papSelectionLabel}: ${data.papQuantity}`}
                  totalPriceText={labels.totalPriceText}
                />
              </StickyComponent>
            </div>
          </div>
          {/* Lead party member */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <h2 className="Booking_groupTitle">{labels.partyDetailsFieldsetHeaderText}</h2>
              <fieldset className="Booking_item">
                <div className="Booking_heading">
                  <h3 className="Booking_itemTitle">{labels.leadPartyMemberFieldsetTitle}</h3>
                  <h4 className="Booking_itemSubTitle">{labels.leadPartyMemberFieldsetSubTitle}</h4>
                </div>
                <MemberDetails
                  fieldNamePrefix="paymentUser"
                  fieldset={data.paymentUser}
                  isDisabled
                  titleOptions={labels.titleOptions}
                />
              </fieldset>
              <fieldset className="Booking_item">
                <div className="Booking_heading">
                  <h4 className="Booking_itemTitle Booking_itemTitle-small">
                    {labels.addressFieldsetTitle}
                  </h4>
                </div>
                <MemberAddress
                  fieldNamePrefix="paymentUser"
                  fieldset={data.paymentUser}
                  isDisabled
                  addressLine2PlaceholderText={labels.placeholderTexts.addressLine2PlaceholderText}
                  telephonePlaceholderText={labels.placeholderTexts.telephonePlaceholderText}
                />
              </fieldset>
            </div>
          </div>
          {/* Terms and conditions */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <TermsConditions
                handleCheckboxChange={this.handleTermsConditionsChange}
                signUpAccepted1={marketingSignUp1}
                signUpAccepted2={marketingSignUp2}
                signUpAccepted3={marketingSignUp3}
                signUpAccepted4={marketingSignUp4}
                signUpLabelText1={labels.marketingSignUp1LabelText}
                signUpLabelText2={labels.marketingSignUp2LabelText}
                signUpLabelText3={labels.marketingSignUp3LabelText}
                signUpLabelText4={labels.marketingSignUp4LabelText}
                termsConditionsAccepted={termsConditionsAccepted}
                termsConditionsLabelText={labels.termsConditionsLabelText}
                termsConditionsText={labels.termsConditionsText}
                title={labels.termsConditionsHeaderText}
              />
            </div>
          </div>
          {/* Payment options */}
          {data.bankTransferPaymentsEnabled && (
            <div className="Booking_group Booking_group-inset">
              <div className="Booking_content">
                <h2 className="Booking_groupTitle">{labels.paymentFieldsetTitle}</h2>
                <fieldset className="Booking_item">
                  <PaymentOptions
                    handleChange={this.handlePaymentOptionsChange}
                    hasBankPaymentOption={data.bankTransferPaymentsEnabled}
                    preferredCardImage={labels.preferredCardImage}
                    price={price}
                    priceText={labels.totalToPayText}
                    currencySymbol={currentCurrency.symbol}
                    optionBankOptionText={labels.paymentOptionBankTransferPayInFullText}
                    optionCardOptionText={labels.paymentOptionCardPayInFullText}
                    selectedValue={this.state.paymentOption}
                  />
                </fieldset>
              </div>
            </div>
          )}
          {/* Submit */}
          <div className="Booking_group" ref={this.submitContainerRef}>
            {errorMessage && (
              <div className="Booking_item">
                <div className="Booking_group">
                  <ErrorMessage text={errorMessage} />
                </div>
              </div>
            )}
            <div className="Booking_item Booking_item-center">
              <Button
                buttonType="submit"
                handleClick={this.handleSubmit}
                isLoading={isLoading}
                modifier="fill"
                text={labels.submitButtonText}
              />
            </div>
          </div>
        </form>
      </section>
    );
  }
}

PapBookingSummary.propTypes = propTypes;
PapBookingSummary.defaultProps = defaultProps;

export default withErrorBoundary(PapBookingSummary);
